<template>
  <div id="live-player">
    <div class="input-url" v-show="!isFullscreen">
      <n-input v-model:value="options.url" placeholder="请输入RTE链接" clearable />
      <n-button @click="createLivePlayer">创建</n-button>
      <n-button :disabled="!isCreated" @click="switchPlayerUrl">切换链接</n-button>
      <n-button :disabled="!isCreated" @click="switchMediaSource">切换模式</n-button>
      <n-button @click="resetLivePlayer">重置</n-button>
    </div>
    <div class="content">
      <div class="content-left">
        <div class="content-video" :class="{ fullscreen: isFullscreen }">
          <div class="video-loading" v-show="isLoading && !isFullscreen">
            <div class="loading-spinner"></div>
          </div>
          <video
            id="remote-video"
            :class="{ fullscreen: isFullscreen }"
            style="object-fit: cover"
            poster="https://imgs.699pic.com/images/501/361/351.jpg!list1x.v2"
          ></video>
        </div>
        <div class="content-operation" v-show="!isFullscreen">
          <n-button v-if="isAutoplayFailed" @click="togglePlayManually" type="warning">
            手动触发播放
          </n-button>
          <n-button
            @click="play"
            :loading="isPending"
            :class="{ 'disabled-look': !isCreated || isPending || isPlaying }"
            type="info"
          >
            播放
          </n-button>
          <n-button @click="playAudioTag" type="info">播放playAudioTag</n-button>
          <n-button @click="playAudioContext" type="info">播放AudioContext</n-button>
          <template></template>
          <n-button :class="{ 'disabled-look': !isPlaying }" type="info" @click="pause">
            暂停
          </n-button>
          <n-button :class="{ 'disabled-look': isStopped || isPending }" type="info" @click="stop">
            停止
          </n-button>
          <n-button :class="{ 'disabled-look': !isCreated }" type="info" @click="mute">
            {{ isMuted ? "恢复" : "静音" }}
          </n-button>
          <n-button :class="{ 'disabled-look': !isCreated }" type="info" @click="retry">
            重试
          </n-button>
          <n-button :class="{ 'disabled-look': !isCreated }" type="error" @click="destroy">
            销毁
          </n-button>
          <n-button type="info" @click="reconnect">重连</n-button>
          <!-- <n-button type="info" @click="tryNext">tryNext</n-button> -->
          <!-- <n-button type="info" @click="tryNext2">修改 uid 为 1234567 并且tryNext</n-button> -->
        </div>
      </div>
      <div class="content-right" v-show="!isFullscreen">
        <div class="content-parameters">
          <div class="content-tag">
            <n-tag :type="isRTCSupport ? 'success' : 'default'" :bordered="false">
              WebRTC {{ isRTCSupport ? "✅" : "❌" }}
            </n-tag>
            <n-tag :type="isHLSSupport ? 'success' : 'default'" :bordered="false">
              HLS {{ isHLSSupport ? "✅" : "❌" }}
            </n-tag>
            <template v-if="mediaSource === 'rtc'">
              <n-tag v-if="isCreated" type="info" :bordered="false" @click="updateNetworkQuality()">
                网络：{{ networkQuality }}
              </n-tag>
              <n-tag v-if="isCreated && rtcMediaStatus" type="warning" :bordered="false">
                媒体：{{ rtcMediaStatus }}
              </n-tag>
            </template>
          </div>
          <div class="content-shape">
            <n-input-number
              v-model:value="options.width"
              :show-button="false"
              placeholder="视频宽度"
            />
            <n-input-number
              v-model:value="options.height"
              :show-button="false"
              placeholder="视频高度"
            />
            <n-input-number
              v-model:value="options.timeout"
              :show-button="false"
              placeholder="超时时间"
            />
          </div>
          <n-select
            placeholder="选择视频模式"
            v-model:value="options.objectFit"
            :options="[
              { label: 'contain', value: 'contain' },
              { label: 'cover', value: 'cover' },
              { label: 'fill', value: 'fill' },
            ]"
          />
          <n-select
            v-model:value="selectedPrivateParam"
            placeholder="搜索常用参数或输入自定义参数名称"
            :options="searchableParamsOptions"
            filterable
            remote
            :filter="filterParams"
            @update:value="handlePrivateParamChange"
            @search="handleSearch"
            tag
          />
          <n-input
            v-model:value="privateParamValue"
            placeholder="输入参数值 (布尔值: true/false, 对象: {...}, 数组: [...], 字符串: 直接输入)"
          />
          <n-button type="info" @click="setPrivateParam">设置私有参数</n-button>
          <div v-if="isParamsRestored">
            <span>已设置参数：</span>
            <n-tag
              v-for="param in setPrivateParams"
              :key="param"
              closable
              @close="removePrivateParam(param)"
            >
              {{ param }}: {{ getParamValueString(param) }}
            </n-tag>
          </div>
          <div v-else>
            <span>已设置参数：</span>
            <span style="color: #999">正在恢复参数...</span>
          </div>
          <n-checkbox v-model:checked="options.autoSwitchHLS">自动切换到HLS</n-checkbox>
          <n-checkbox v-model:checked="options.defaultUseHLS">默认使用HLS</n-checkbox>
          <n-checkbox v-model:checked="options.autoplay">自动播放</n-checkbox>
          <n-checkbox v-model:checked="options.mirror">镜像播放</n-checkbox>
          <n-checkbox v-model:checked="options.loop">是否loop</n-checkbox>
          <n-checkbox v-model:checked="options.noSleep">NoSleep</n-checkbox>
        </div>
        <div class="content-stats">
          <div class="content-text-mode">当前模式：{{ mediaSource.toUpperCase() }}</div>
          <div>用户ID：{{ userId }}</div>
          <template v-if="isCreated && mediaSource === 'rtc'">
            <div>连接状态：{{ rtcConnectionState }}</div>
            <div>频道内用户状态：{{ remoteUserState }}</div>
            <div>主播ID：{{ hostId }}</div>
            <div>主播状态：{{ hostState }}</div>
            <div>主播切换状态：{{ hostChangeState }}</div>
            <div>主播视频状态：{{ hostVideoState }}</div>
            <div>主播音频状态：{{ hostAudioState }}</div>
            <div>video 标签状态{{ videoStatus }}</div>
            <div>video 标签时间{{ videoTime }}</div>
            <div class="dialog-stats">
              <n-button type="info" dashed @click="showRTCStats" size="tiny">获取统计信息</n-button>
            </div>
          </template>
          <template v-if="isCreated && mediaSource !== 'rtc'">
            <div>video 标签状态{{ videoStatus }}</div>
            <div>video 标签时间{{ videoTime }}</div>
            <template v-if="isCreated && mediaSource === 'file'">
              <label for="progress">设置进度：</label>
              <input type="range" id="progress" v-model="currentTime" min="0" max="100" />
              <span>{{ currentTime }}%</span>
            </template>
          </template>
          <template v-if="isCreated">
            <div>
              <label for="progress">音量：{{ volume }}</label>
            </div>
            <div>
              <label for="progress">设置音量：</label>
              <input type="range" id="progress" v-model="currentVolume" min="0" max="500" />
              <span>{{ currentVolume / 100 }}</span>
            </div>
          </template>
          <div class="content-model-stats">
            <n-modal
              v-model:show="isRTCStatsVisible"
              class="model-stats"
              preset="card"
              title="RTC 统计信息"
              :onAfterLeave="closeRTCStats"
            >
              <n-data-table
                :columns="[
                  { title: '属性', key: 'key', minWidth: 140 },
                  { title: '音频', key: 'audio', minWidth: 120 },
                  { title: '视频', key: 'video', minWidth: 120 },
                ]"
                :data="rtcMediaStats"
              />
              <template #footer>
                <n-button text tag="a" :href="RTC_AUDIO_STATS_DOC" target="_blank" type="info">
                  音频统计信息文档
                </n-button>
                <n-space />
                <n-button text tag="a" :href="RTC_VIDEO_STATS_DOC" target="_blank" type="info">
                  视频统计信息文档
                </n-button>
              </template>
            </n-modal>
          </div>
        </div>
      </div>
    </div>
    <div class="bottom-input">
      <n-input placeholder="请输入内容" clearable />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NInput,
  NInputNumber,
  NButton,
  NCheckbox,
  NModal,
  NSelect,
  NDataTable,
  NTag,
  NSpace,
  useNotification,
} from "naive-ui";
import { h, computed, onBeforeUnmount, reactive, ref, onMounted } from "vue";
import {
  LivePlayer,
  PlayerEvent,
  RtcSourceState,
  RtcMediaState,
  RtcUserState,
  MediaSource,
  MediaPlayState,
  enableLogUpload,
  enableNewNetworkConfig,
  isHlsSupported,
  isRtcSupported,
  setParameter,
  type RtcUser,
  type IPlayerOptions,
  type RtcVideoStats,
  type RtcAudioStats,
  type IPlayerError,
  RtcEvent,
  PlayerErrorCode,
  setRTCParameter,
  installModule,
  getRTCParameter,
} from "agora-fls-sdk";

import { watch } from "vue";
import { MUTABLE_PARAMS } from "@agora-js/shared";

/* @preserve */
const randomBlockMainThread = (duration: number) => {
  console.log(`开始随机阻塞主线程 ${duration / 1000} 秒...`);
  const startTime = Date.now();
  const endTime = startTime + duration;
  let result = 0;
  while (Date.now() < endTime) {
    for (let i = 0; i < 1000000; i++) {
      result += Math.sqrt(i) * Math.sin(i);
    }
  }
  console.log(startTime, endTime, result);

  console.log(`随机阻塞主线程完成，总耗时: ${Date.now() - startTime}ms`);
};

// setRTCParameter("ENABLE_INSTANT_VIDEO", true);

// setRTCParameter("ENABLE_PREALLOC_PC", true);

// window.test = () => {
// this.handleNotification({
//   action: "tryNext",
//   code: 30,
//   detail: "fallback_hls",
//   option: "",
// });
//     };

setParameter("VIDEO_FROZEN_POINTS", 3);

const RTC_AUDIO_STATS_DOC =
  "https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/remoteaudiotrackstats";
const RTC_VIDEO_STATS_DOC =
  "https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/remotevideotrackstats";

enableLogUpload();
enableNewNetworkConfig();

// EVENT_REPORT_DOMAIN: "183-131-160-209.edge.staging-1-aws.myagoralab.com",
// EVENT_REPORT_BACKUP_DOMAIN: "183-131-160-209.edge.staging-1-aws.myagoralab.com",

const notification = useNotification();

// http://vjs.zencdn.net/v/oceans.mp4
// https://media.w3.org/2010/05/sintel/trailer.mp4

// 从URL中提取rte参数的函数
const urlParams = new URLSearchParams(window.location.search);
const rteUrl = urlParams.get("url") || "";
const autoplay = urlParams.get("autoplay") || 0;
const fullscreen = urlParams.get("fullscreen") || 0;
console.log("rteUrl", rteUrl);
console.log("autoplay", autoplay);
console.log("fullscreen", fullscreen);

const appId = "xxxxxxxxxxxxxxxxxxxxxx";

// rte://{appid}/{appname}/{channelname}?token={token}&uid={uid}

// rte://edca9ba1d5c945d2a031370981a58892/1179742?token=007eJxTYOheyjtl85W4Au0fu744Xft5d9mqjP8OIi4nFq5qPf+6/be4AkNqSnKiZVKiYYppsqWJaYpRooGxobG5gaWFYaKphYWl0Rrmk+kBbgwMHu8/FjEyMDKwADGIzwQmmcEkC5hkZzA0NLc0NzFiYAAAYdUk3Q==&uid=571510081

onBeforeUnmount(() => {
  destroy();
});

onMounted(async () => {
  // 先恢复已保存的私有参数值
  await restorePrivateParams();

  // 初始化常用参数选项
  handleSearch("");

  if (autoplay) {
    createLivePlayer();
  }
  setInterval(() => {
    const remoteVideo = document.getElementById("remote-video") as HTMLVideoElement;
    videoStatus.value = remoteVideo?.readyState + "";
    videoTime.value = remoteVideo?.currentTime + "";
    volume.value = player?.getVolume() || 0;
  }, 1000);
});

const rtcConnectionState = ref("");
const hostVideoState = ref("");
const hostAudioState = ref("");
const remoteUserState = ref("");
const hostChangeState = ref("");
const hostState = ref("");
const hostId = ref("");
const userId = ref(0);
const mediaSource = ref<MediaSource>(MediaSource.RTC);
const networkQuality = ref("未知");
const rtcMediaStatus = ref("");
const isRTCSupport = isRtcSupported();
const isHLSSupport = isHlsSupported();
const videoStatus = ref("");
const videoTime = ref("");
const volume = ref(1);
const currentTime = ref(0);
const currentVolume = ref(100);

// 私有参数相关字段
const selectedPrivateParam = ref<string>("");
const privateParamValue = ref<string>("");

// 从 localStorage 读取已设置的私有参数
const storedPrivateParams = localStorage.getItem("__private_params__");
const setPrivateParams = ref<string[]>(storedPrivateParams ? JSON.parse(storedPrivateParams) : []);

// 跟踪参数是否已恢复完成
const isParamsRestored = ref(false);

// 常用参数列表
// const commonParams = [
//   "ENABLE_INSTANT_VIDEO",
//   "ENABLE_PRE_SUB",
//   "WEBCS_DOMAIN",
//   "WEBCS_DOMAIN_BACKUP_LIST",
//   "GATEWAY_ADDRESS",
//   "CLIENT_ROLE_OPTIONS",
// ];
const commonParams = Object.keys(MUTABLE_PARAMS);

// 可搜索的参数选项
const searchableParamsOptions = ref<Array<{ label: string; value: string }>>([]);

// 过滤参数函数
function filterParams(pattern: string, option: { label: string; value: string }) {
  return (
    option.label.toLowerCase().includes(pattern.toLowerCase()) ||
    option.value.toLowerCase().includes(pattern.toLowerCase())
  );
}

// 处理搜索
function handleSearch(query: string) {
  const options: Array<{ label: string; value: string }> = [];

  if (!query) {
    // 没有输入时显示所有常用参数
    commonParams.forEach((param) => {
      options.push({ label: param, value: param });
    });
  } else {
    // 有输入时只显示匹配的常用参数
    commonParams.forEach((param) => {
      if (param.toLowerCase().includes(query.toLowerCase())) {
        options.push({ label: param, value: param });
      }
    });
  }

  searchableParamsOptions.value = options;
}

// 处理输入私有参数名称
function handlePrivateParamChange(value: string) {
  selectedPrivateParam.value = value;

  // 如果输入框为空，清空值输入框
  if (!value.trim()) {
    privateParamValue.value = "";
    return;
  }

  // 获取当前参数的值并显示在输入框中
  const currentValue = getRTCParameter(value);
  if (currentValue !== undefined && currentValue !== null) {
    if (typeof currentValue === "object") {
      // 对象或数组转换为 JSON 字符串
      privateParamValue.value = JSON.stringify(currentValue, null, 2);
    } else {
      // 布尔值或字符串直接转换为字符串
      privateParamValue.value = String(currentValue);
    }
  } else {
    // 如果参数未设置，清空输入框
    privateParamValue.value = "";
  }
}

// 设置私有参数
function setPrivateParam() {
  if (!selectedPrivateParam.value || !privateParamValue.value) return;

  let paramValue: any;

  // 尝试解析不同类型的值
  try {
    // 尝试解析为 JSON（对象或数组）
    paramValue = JSON.parse(privateParamValue.value);
  } catch {
    // 如果不是有效的 JSON，尝试解析为布尔值
    const lowerValue = privateParamValue.value.toLowerCase();
    if (lowerValue === "true" || lowerValue === "false") {
      paramValue = lowerValue === "true";
    } else {
      // 否则作为字符串处理
      paramValue = privateParamValue.value;
    }
  }

  // 设置参数
  setRTCParameter(selectedPrivateParam.value, paramValue);

  // 记录已设置的参数
  if (!setPrivateParams.value.includes(selectedPrivateParam.value)) {
    setPrivateParams.value.push(selectedPrivateParam.value);
  }

  // 保存到 localStorage
  localStorage.setItem("__private_params__", JSON.stringify(setPrivateParams.value));

  // 保存参数值到 localStorage
  const storedParamValues = localStorage.getItem("__private_param_values__");
  const paramValues = storedParamValues ? JSON.parse(storedParamValues) : {};
  paramValues[selectedPrivateParam.value] = paramValue;
  localStorage.setItem("__private_param_values__", JSON.stringify(paramValues));

  privateParamValue.value = "";
}

// 移除已设置的私有参数
function removePrivateParam(param: string) {
  setPrivateParams.value = setPrivateParams.value.filter((p) => p !== param);
  // 重置参数为默认值（通常是 undefined 或 null）
  setRTCParameter(param, undefined);
  // 更新 localStorage
  localStorage.setItem("__private_params__", JSON.stringify(setPrivateParams.value));

  // 从 localStorage 中移除参数值
  const storedParamValues = localStorage.getItem("__private_param_values__");
  if (storedParamValues) {
    const paramValues = JSON.parse(storedParamValues);
    delete paramValues[param];
    localStorage.setItem("__private_param_values__", JSON.stringify(paramValues));
  }
}

// 获取参数当前值的字符串表示
function getParamValueString(param: string): string {
  // 首先尝试从 localStorage 中获取保存的值
  const storedParamValues = localStorage.getItem("__private_param_values__");
  if (storedParamValues) {
    try {
      const paramValues = JSON.parse(storedParamValues);
      if (paramValues[param] !== undefined) {
        const value = paramValues[param];
        if (typeof value === "object") {
          return JSON.stringify(value);
        }
        return String(value);
      }
    } catch (error) {
      console.error("读取保存的参数值失败:", error);
    }
  }

  // 如果 localStorage 中没有，再尝试从 RTC 参数中获取
  const value = getRTCParameter(param);
  if (value === undefined || value === null) {
    return "未设置";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

// 恢复已保存的私有参数值
async function restorePrivateParams() {
  // 从 localStorage 读取已保存的参数值
  const storedParamValues = localStorage.getItem("__private_param_values__");
  if (storedParamValues) {
    try {
      const paramValues = JSON.parse(storedParamValues);
      // 恢复每个参数的值
      for (const [param, value] of Object.entries(paramValues)) {
        setRTCParameter(param, value);
        // 添加小延迟确保参数设置完成
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
      console.log("私有参数恢复完成:", Object.keys(paramValues));
    } catch (error) {
      console.error("恢复私有参数值失败:", error);
    }
  }
  isParamsRestored.value = true;
}

const rtcAudioStats = ref<RtcAudioStats | undefined>();
const rtcVideoStats = ref<RtcVideoStats | undefined>();

watch(
  mediaSource,
  (source) => {
    document.title = `FLS 播放器 | ${source.toUpperCase()} 模式`;
  },
  { immediate: true }
);

const rtcMediaStats = computed(() => {
  const statKeys = Object.keys({ ...rtcAudioStats.value, ...rtcVideoStats.value });

  if (statKeys) {
    return statKeys.map((key) => {
      let audio = rtcAudioStats.value?.[key] ?? "-";
      let video = rtcVideoStats.value?.[key] ?? "-";

      if (typeof audio === "number" && String(audio).includes(".")) {
        audio = audio.toFixed(4);
      }
      if (typeof video === "number" && String(video).includes(".")) {
        video = video.toFixed(4);
      }

      return {
        key,
        audio,
        video,
      };
    });
  }

  return [];
});

const playState = ref<MediaPlayState>(MediaPlayState.STOPPED);

const isCreated = ref(false);
const isStopped = computed(() => playState.value === "stopped");
const isPending = computed(() => playState.value === "pending");
const isPaused = computed(() => playState.value === "paused");
const isPlaying = computed(() => playState.value === "playing");

const isAutoplayFailed = ref(false);
const isRTCStatsVisible = ref(false);

const isFullscreen = ref(fullscreen);

let player: LivePlayer | undefined = undefined;
const storedOptions = JSON.parse(localStorage.getItem("__player_options__"));
const defaultOptions: IPlayerOptions = {
  url: `rte://${appId}/an/sn?token=null&uid=${Math.floor(Math.random() * 100000)}`,
  el: "remote-video",
  width: undefined,
  height: undefined,
  aspectRatio: "16/9",
  autoplay: true,
  objectFit: "cover",
  mirror: false,
  defaultUseHLS: false,
  autoSwitchHLS: true,
  hlsConfig: undefined,
  timeout: 3000,
  loop: false,
  noSleep: false,
};

const options = reactive<IPlayerOptions>(storedOptions || defaultOptions);

if (rteUrl) {
  options.url = rteUrl;
}

// 添加 loading 状态
const isLoading = ref(false);

function resetLivePlayer() {
  destroy();

  localStorage.removeItem("__player_options__");
  Object.keys(defaultOptions).forEach((key) => {
    options[key] = defaultOptions[key];
  });
}

watch(
  currentTime,
  (value) => {
    console.info("currentTime", value);
    const duration = player?.duration;

    if (duration) {
      player.currentTime = (value / 100) * duration;
    }
  },
  { immediate: true }
);

watch(
  currentVolume,
  (value) => {
    player && player.setVolume(value / 100);
  },
  { immediate: true }
);

async function createLivePlayer() {
  setRTCParameter("SHOW_GLOBAL_CLIENT_LIST", true);

  setRTCParameter("ENABLE_PRE_SUB", true);
  setRTCParameter("ENABLE_INSTANT_VIDEO", true);
  setRTCParameter("EXPERIMENTS", {
    enableDuplicatedIframe: true,
  });
  setRTCParameter("ENABLE_PRE_SUB_WITH_PRE_PC", true);
  setRTCParameter("ENABLE_PRE_RENDER", true);

  // setRTCParameter("EVENT_REPORT_DOMAIN", "183-131-160-209.edge.staging-1-aws.myagoralab.com");
  // setRTCParameter(
  //   "EVENT_REPORT_BACKUP_DOMAIN",
  //   "183-131-160-209.edge.staging-1-aws.myagoralab.com"
  // );
  // setRTCParameter("GATEWAY_ADDRESS", [{ ip: "122.228.77.225", port: 8971 }]);
  // setRTCParameter(
  //   "FINGERPRINT",
  //   "9C:91:F2:EB:13:2B:C1:C6:79:09:20:B2:32:55:69:14:B4:1E:FD:F5:BD:98:30:D8:45:36:11:EE:F3:E4:2B:D1"
  // );

  // setRTCParameter("GATEWAY_ADDRESS", [{ ip: "122.228.77.225", port: 8970 }]);
  // setRTCParameter(
  //   "FINGERPRINT",
  //   "39:16:B0:DD:56:08:0F:47:76:53:CC:E6:50:53:0B:BD:99:13:60:23:C1:28:EC:8B:BA:C7:26:90:9A:5C:E0:B7"
  // );

  // setRTCParameter("ENABLE_VOS_FALLBACK", true);
  // fetch("https://media.w3.org/2010/05/sintel/trailer.mp4", {
  //   mode: "no-cors",
  // })
  //   .then((res) => {
  //     console.error("res", res);
  //   })
  //   .catch((err) => {
  //     console.error("err", err);
  //   });
  // return;
  // console.error("test");
  // setRTCParameter("WEBCS_DOMAIN", ["http-cn.ap.staging-1-ali.myagoralab.com"]);
  // setRTCParameter("WEBCS_DOMAIN_BACKUP_LIST", []);
  // setRTCParameter("GATEWAY_ADDRESS", [
  //   {
  //     ip: "120.195.180.30",
  //     port: 19305,
  //   },
  // ]);
  // setRTCParameter("GATEWAY_DOMAINS", ["edge.staging-1-aws.myagoralab.com"]);

  console.info("LivePlayer createLivePlayer: ", options);

  player && (await destroy());
  // isLoading.value = false; // 创建时显示 loading

  rtcMediaStatus.value = "";
  const video = document.getElementById("remote-video") as HTMLVideoElement;
  // video.setAttribute("playsinline", true);
  // video.setAttribute("webkit-playsinline", true);
  // options.rtcConfig = {
  //   mode: "live",
  //   codec: "h264",
  //   clientRoleOptions: { level: 1, delay: 3000 },
  // };

  try {
    player = new LivePlayer({ ...options });
    // @ts-ignore
    window.player = player;
  } catch (error) {
    console.error("LivePlayer createLivePlayer error: ", error);
    notification.error({ title: "创建播放器出错", content: error.message || error.name || error });
    isLoading.value = false;
    throw error;
  }

  playState.value = player.playState;

  // Player
  player.on(PlayerEvent.AUTOPLAY_PREVENTED, onAutoPlayFailed);
  player.on(PlayerEvent.NETWORK_QUALITY, onNetworkQuality);
  player.on(PlayerEvent.BEFORE_MEDIA_SOURCE_CHANGE, onBeforeMediaSourceChange);
  player.on(PlayerEvent.MEDIA_SOURCE_CHANGED, onMediaSourceChanged);
  player.on(PlayerEvent.PLAY_STATE_CHANGED, onPlayerStateChanged);
  player.on(PlayerEvent.REQUEST_SWITCH_MEDIA_SOURCE, onRequestSwitchMediaSource);

  player.on(PlayerEvent.ERROR, onError);
  player.on(PlayerEvent.USER_ASSIGNED, (uid: number | string) => {
    console.log("user assigned", uid);
    userId.value = Number(uid);
  });

  // RTC
  player.on(PlayerEvent.RTC_HOST_CHANGED, onRTCHostChanged);
  player.on(PlayerEvent.RTC_USER_STATE_CHANGED, onRTCUserStateChanged);

  player.on(PlayerEvent.RTC_MEDIA_CHANGED, ({ type, state }) => {
    if (type === "video") onRTCVideoStateChanged(state);
    if (type === "audio") onRTCAudioStateChanged(state);
  });

  player.on(PlayerEvent.RTC_SOURCE_STATE_CHANGED, onRTCStateChanged);

  // @ts-ignore
  mediaSource.value = player.source?.mode;
  isCreated.value = true;

  localStorage.setItem("__player_options__", JSON.stringify(options));
  window["player"] = player;

  player.on(PlayerEvent.RTC_EVENTS, (code: RtcEvent) => {
    switch (code) {
      case RtcEvent.NETWORK_EXCEPTION:
        notification.error({ title: "网络异常，请检查网络连接", duration: 2000 });
        break;
      case RtcEvent.NETWORK_EXCEPTION_RECOVERED:
        notification.info({ title: "网络异常已恢复", duration: 2000 });

        break;
      case RtcEvent.VIDEO_DECODED_FAILED:
        rtcMediaStatus.value = "视频解码失败";
        notification.error({ title: rtcMediaStatus.value, duration: 2000 });
        break;
      case RtcEvent.VIDEO_DECODED_RECOVERED:
        rtcMediaStatus.value = "";
        notification.info({ title: "视频解码失败恢复", duration: 2000 });
        break;
      case RtcEvent.VIDEO_FROZEN:
        rtcMediaStatus.value = "视频发生卡顿";
        notification.error({ title: rtcMediaStatus.value, duration: 2000 });
        break;
      case RtcEvent.VIDEO_FROZEN_RECOVERED:
        rtcMediaStatus.value = "";
        notification.info({ title: "视频卡顿已恢复", duration: 2000 });
        break;
    }
  });
}

async function switchPlayerUrl() {
  if (player) {
    console.info("LivePlayer switchPlayerUrl: target url ", options.url);
    await player.switchURL(options.url);
  }
}

async function togglePlayManually() {
  await play();
  isAutoplayFailed.value = false;
}

async function play() {
  console.info("LivePlayer play");
  player && (await player.play());
}

function playAudioTag() {
  // @ts-ignore
  const audioTrack = player?.source?.audioTrack;
  if (audioTrack) {
    audioTrack.stop();
    audioTrack._useAudioElement = true;
    audioTrack.play();
    console.log("playWebAudio", audioTrack.getTrackId());
  }
}

function playAudioContext() {
  // @ts-ignore
  const audioTrack = player?.source?.audioTrack;
  if (audioTrack) {
    audioTrack.stop();
    audioTrack._useAudioElement = false;
    audioTrack.play();
    console.log("playWebAudio", audioTrack.getTrackId());
  }
}

async function pause() {
  console.info("LivePlayer pause");
  player && (await player.pause());
}

const isMuted = ref(false);
function mute() {
  isMuted.value = !isMuted.value;
  player && player.setVolume(isMuted.value ? 0 : 1);
}

async function stop() {
  console.info("LivePlayer stop");
  player && (await player.pause(true));
}

async function retry() {
  console.info("LivePlayer retry");
  player && (await player.retry());
}

async function destroy() {
  console.info("LivePlayer destroy");
  player && (await player.destroy());
  player = undefined;
  isCreated.value = false;
  isMuted.value = false;

  resetRTCStats();
}

function resetRTCStats() {
  rtcConnectionState.value = "";
  hostVideoState.value = "";
  hostAudioState.value = "";
  remoteUserState.value = "";
  hostChangeState.value = "";
  hostState.value = "";
  hostId.value = "";
  networkQuality.value = "未知";
}

async function switchMediaSource() {
  if (player) {
    console.info("LivePlayer switchMediaSource");
    const target = mediaSource.value === MediaSource.RTC ? MediaSource.HLS : MediaSource.RTC;
    await player.switchMediaSource(target);
  }
}

const qualities = ["未知", "极好", "良好", "一般", "较差", "很差", "断开"];
function updateNetworkQuality(quality?: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
  if (player && quality == null) {
    quality = player.getNetworkQuality();
    console.info("LivePlayer updateNetworkQuality: ", qualities[quality || 0]);
  }

  networkQuality.value = qualities[quality || 0];
}

let rtcStatsTimer: number | undefined;
function updateRTCStats() {
  const stats = player && player.getStats();
  if (stats) {
    rtcAudioStats.value = stats.audio;
    rtcVideoStats.value = stats.video;
  }
}
function showRTCStats() {
  if (player && mediaSource.value === MediaSource.RTC) {
    rtcStatsTimer && clearInterval(rtcStatsTimer);
    rtcStatsTimer = setInterval(updateRTCStats, 1000);
    updateRTCStats();

    isRTCStatsVisible.value = true;
  }
}

function closeRTCStats() {
  isRTCStatsVisible.value = false;

  rtcStatsTimer && clearInterval(rtcStatsTimer);
  rtcAudioStats.value = undefined;
  rtcVideoStats.value = undefined;
}

function setVolume(value: number) {
  player && player.setVolume(value);
}

// 事件监听
function onBeforeMediaSourceChange(target: MediaSource) {
  console.info("LivePlayer onBeforeMediaSourceChange to ", target);

  notification.info({ title: `当前播放模式即将切换到 ${target}`, duration: 3000 });
}
function onMediaSourceChanged(target: MediaSource) {
  console.info("LivePlayer onMediaSourceChanged to ", target);

  notification.info({ title: `当前播放模式已切换到 ${target}`, duration: 3000 });

  if (target === MediaSource.RTC) {
  }

  if (target === MediaSource.HLS) {
    resetRTCStats();
  }

  mediaSource.value = target;
}

// RTC
function onRTCHostChanged(to?: RtcUser, from?: RtcUser) {
  console.info("LivePlayer onRTCHostChanged: ", from, " => ", to);

  hostChangeState.value = `${from?.uid} => ${to?.uid}`;
  hostId.value = String(to?.uid);
  hostState.value = "changed";
}

function onRTCUserStateChanged(data: {
  user: any;
  state: RtcUserState;
  isHost: boolean;
  mediaType?: "audio" | "video";
}) {
  console.info("LivePlayer onRTCUserStateChanged: ", data);

  remoteUserState.value = `${data.user.uid}:${data.state}`;

  if (data.isHost) {
    hostState.value = data.state + (data.mediaType ? `: ${data.mediaType}` : "");
  }
}

function onRTCVideoStateChanged(state: RtcMediaState) {
  hostVideoState.value = state;
  if (state === RtcMediaState.EMPTY || state === RtcMediaState.STOPPED) {
    rtcMediaStatus.value = "";
  }
}
function onRTCAudioStateChanged(state: RtcMediaState) {
  hostAudioState.value = state;
}
function onRTCStateChanged(state: RtcSourceState) {
  rtcConnectionState.value = state;
}

let autoplayFailedCount = 0;

// Player
function onAutoPlayFailed() {
  autoplayFailedCount++;
  notification.error({ title: `自动播放失败，请手动点击播放 ${autoplayFailedCount}` });
  isAutoplayFailed.value = true;
}
function onNetworkQuality(quality: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
  updateNetworkQuality(quality);
}
function onRequestSwitchMediaSource(source?: MediaSource) {
  console.warn("LivePlayer request switch media source to: ", source);

  if (options.autoSwitchHLS && source === "hls") {
    player && player.switchMediaSource(source);
  } else {
    const nCallback = notification.error({
      title: "发生不可恢复错误",
      content: () =>
        source
          ? h("div", [
              "播放器请求 ",
              h(
                NButton,
                {
                  onClick: () => {
                    nCallback.destroy();
                    switchMediaSource();
                  },
                  type: "info",
                  text: true,
                },
                "切换"
              ),
              ,
              ` 到${source?.toUpperCase()}模式`,
            ])
          : h("div", ["autoSwitchHLS 为false， 请自行切换链路试试... "]),
    });
  }
}
function onPlayerStateChanged(state: MediaPlayState) {
  console.info("LivePlayer onPlayerStateChanged: ", state);
  playState.value = state;

  isLoading.value = state === "playing" || state === "paused" || state === "stopped" ? false : true;
}

function onError({ error, source }: IPlayerError) {
  console.error("LivePlayer onError: ", error);
  if ("code" in error && error.code === PlayerErrorCode.TIMEOUT) {
    notification.error({
      title: `${source} 模式下错误，请重试`,
      content: error.message,
    });
  }
}
function reconnect() {
  if (player) {
    // @ts-ignore
    const client = player.source.rtc;
    client._p2pChannel.disconnectForReconnect();
    client._gateway.signal.websocket.websocket.close();
  }
}
</script>

<style lang="less">
#live-player {
  max-width: 100%;
  margin-top: 24px;
  height: 200vh;
  padding: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.06);

  .input-url {
    display: flex;
    margin: 12px;
    > .n-input {
      margin-right: 12px;
    }
    > button {
      margin-left: 12px;
    }
  }
  .content {
    margin: 6px 0;
    display: flex;
    flex-wrap: wrap;
    .content-left,
    .content-right {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      padding: 6px 12px;
      box-sizing: border-box;
      max-width: 100%;
    }
    .content-left {
      .content-video {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 12px;
        border-radius: 4px;
        width: 640px;
        max-width: 100%;
        aspect-ratio: 16/9;
        background-color: #efefef;
        position: relative;
        > video {
          border-radius: 4px;
          width: 100%;
          max-height: 100%;
          aspect-ratio: 16/9;
          background-image: url("https://imgs.699pic.com/images/501/361/351.jpg!list1x.v2");
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;
        }
      }
      .content-operation {
        display: flex;
        flex-wrap: wrap;
        margin-top: 12px;
        > button {
          margin-left: 12px;
        }
        > button:first-child {
          margin-left: 0 !important;
        }
      }
    }
    .content-right {
      .content-parameters {
        margin-bottom: 6px;
        > .n-input,
        > .n-select,
        > .n-checkbox {
          margin-bottom: 6px;
        }

        > .content-tag {
          margin-bottom: 12px;
          > .n-tag {
            margin-right: 8px;
          }
        }
        > .content-shape {
          display: flex;
          margin-bottom: 6px;
          > .n-input-number {
            flex: 1;
            &:first-child {
              margin-right: 6px;
            }
          }
        }
      }

      .content-stats {
        margin-top: 12px;
        > div {
          display: flex;
          align-items: center;
          margin: 2px 0;
        }
        > .content-text-mode {
          font-weight: bold;
        }
        > .dialog-stats {
          margin-top: 8px;
        }
      }
    }
  }

  .content-video {
    .video-loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10;
      border-radius: 4px;
    }

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      border-top-color: #fff;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  }
}

.model-stats {
  max-width: 640px;
  .n-card__footer {
    text-align: right;
    > a {
      margin-right: 12px;
    }
  }
}

@media (max-width: 688px) {
  #live-player {
    margin-top: 0;
    .input-url {
      flex-wrap: wrap;
      > .n-input {
        margin-right: 0;
        flex-shrink: 0;
      }
      > button {
        margin: 12px 12px 0 0 !important;
      }
    }
  }
}

/* 修改 disabled-look 使其与 Naive UI 的禁用样式一致 */
.disabled-look {
  opacity: 0.5 !important; /* Naive UI 的禁用透明度 */
  cursor: not-allowed !important;
  pointer-events: auto !important; /* 确保仍然可以点击 */
}

/* 针对不同类型按钮的禁用样式 */
.n-button--info.disabled-look {
  color: rgb(51, 54, 57) !important;
  border-color: rgb(46, 51, 56) !important;
  background-color: rgb(249, 250, 251) !important;
  --n-border: rgb(46, 51, 56);
  --n-ripple-color: rgb(46, 51, 56);
}

.n-button--error.disabled-look {
  color: rgb(51, 54, 57) !important;
  border-color: rgb(248, 113, 113) !important;
  background-color: rgb(254, 242, 242) !important;
  --n-border: rgb(248, 113, 113);
  --n-ripple-color: rgb(248, 113, 113);
}

.n-button--primary.disabled-look {
  color: rgb(51, 54, 57) !important;
  border-color: rgb(24, 160, 88) !important;
  background-color: rgb(240, 252, 246) !important;
  --n-border: rgb(24, 160, 88);
  --n-ripple-color: rgb(24, 160, 88);
}

.n-button--warning.disabled-look {
  color: rgb(51, 54, 57) !important;
  border-color: rgb(251, 146, 60) !important;
  background-color: rgb(254, 249, 242) !important;
  --n-border: rgb(251, 146, 60);
  --n-ripple-color: rgb(251, 146, 60);
}

/* 取消悬停效果 */
.disabled-look:hover {
  opacity: 0.5 !important;
  color: inherit !important;
  border-color: inherit !important;
  background-color: inherit !important;
}

/* 鼠标点击时不显示活动状态 */
.disabled-look:active {
  opacity: 0.5 !important;
  color: inherit !important;
  border-color: inherit !important;
  background-color: inherit !important;
}

.fullscreen {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999;
  background: #000;
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  aspect-ratio: unset !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}

.fullscreen video {
  width: 100vw !important;
  height: 100vh !important;
  object-fit: cover !important;
  background: #000;
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  aspect-ratio: unset !important;
}
</style>
